import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { MessageSquare } from "lucide-react";
import { errorMessage } from "@web/lib/api";
import { fmtPhone } from "@web/lib/format";
import { fmtAgo } from "@web/lib/formatLeads";
import { conversationsQuery, type Conversation } from "@web/lib/queries-leads";
import { useDebounced } from "@web/lib/useDebounced";
import { Avatar, Button, ConversationLayout, EmptyState, Page, Row, RowGroup, SearchBar, Skeleton, Stack, StatusChip, Text } from "@web/ui";

/** "You: Thanks for reaching out…" / "Call · No answer" preview for a conversation row. */
function preview(c: Conversation): string {
  const m = c.lastMessage;
  if (!m) return "";
  const body = (m.channel === "email" ? (m.body ?? "") : m.body).replace(/\s+/g, " ").trim();
  if (m.channel === "call") return `Call · ${body}`;
  return `${m.direction === "out" ? "You: " : ""}${body}`;
}

/** Messages (SPEC §6.1 item 11): every conversation, unread first (bold + count), searchable by name / phone / email. */
export function Messages() {
  return <ConversationLayout selected={false} list={<ConversationList />}><Stack variant="thread-empty"><EmptyState icon={MessageSquare} title="Room for a conversation" body="Choose a conversation to see messages, logged calls and your next reply." /></Stack></ConversationLayout>;
}

export function ConversationList() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const debounced = useDebounced(q);
  const list = useQuery(conversationsQuery(debounced));
  // The API returns one row per contact; two contacts can still share a display name, so disambiguate those with the phone.
  const nameCount = new Map<string, number>();
  for (const c of list.data ?? []) nameCount.set(c.contactName?.trim() || "", (nameCount.get(c.contactName?.trim() || "") ?? 0) + 1);

  return (
    <Page as="div">
      <Stack gap={3}>
        <SearchBar value={q} onChange={setQ} placeholder="Search conversations" label="Search conversations" />
        {list.isError ? (
          <EmptyState icon={MessageSquare} title="Conversations unavailable" body={errorMessage(list.error)} action={<Button onClick={() => void list.refetch()}>Retry conversations</Button>} />
        ) : list.isLoading && !list.data ? (
          <Skeleton count={6} />
        ) : !list.data?.length ? (
          <EmptyState icon={MessageSquare} title={debounced ? "No matches" : "No conversations yet"} body={debounced ? "Try a name, phone or email." : "Texts, emails and logged calls with clients show up here."} />
        ) : (
          <RowGroup>
            {list.data.map((c) => {
              const base = c.contactName?.trim() || c.phone || c.email || (c.contactId ? "No name" : "Contact unavailable");
              const name = (nameCount.get(c.contactName?.trim() || "") ?? 0) > 1 && (c.phone || c.email) ? `${base} · ${c.phone ? fmtPhone(c.phone) : c.email}` : base;
              const unread = c.unread > 0;
              return (
                <Row
                  key={c.contactId ?? c.lastMessage.id}
                  selected={Boolean(c.contactId && pathname.endsWith(`/${c.contactId}`))}
                  icon={<Avatar name={name} size="sm" />}
                  title={unread ? <Text as="span" variant="headline">{name}</Text> : name}
                  subtitle={preview(c)}
                  trailing={
                    <Stack gap={1} align="end">
                      <Text as="span" variant="caption" tone={unread ? "accent" : "tertiary"}>
                        {fmtAgo(c.lastAt)}
                      </Text>
                      {unread && <StatusChip tone="accent">{c.unread}</StatusChip>}
                    </Stack>
                  }
                  onClick={c.contactId ? () => navigate({ to: "/more/messages/$contactId", params: { contactId: c.contactId! } }) : undefined}
                />
              );
            })}
          </RowGroup>
        )}
      </Stack>
    </Page>
  );
}
